import { FilterValue } from '@api-types/types/filter-value.type';
import { createReducer, on } from '@ngrx/store';
import { convertFiltersRecordToMap } from '@api-types/helper/helper';
import { FilterState } from './filter.state';
import { FilterActions } from './filter.actions';

export const initialState: FilterState = {
  availableFilters: [],
  allAvailableFilters: undefined,
  selectedFilters: {},
  appliedFilters: {},
  consensusAppliedFilters: {},
  demandSourceAppliedFilters: {},
  savedFilterCombinations: [],
  saveFilterCombinationIsLoading: false,
  deleteFilterCombinationIsLoading: false,
  filterCombinationForecastSeries: {},
  pendingFilterCombinationSeriesIds: [],
};

export const filterReducer = createReducer(
  initialState,
  on(FilterActions.setAvailableFilters, (state, action) => ({
    ...state,
    availableFilters: action.availableFilters,
  })),
  on(FilterActions.setAllAvailableFilters, (state, action) => ({
    ...state,
    allAvailableFilters: action.allAvailableFilters,
  })),
  on(FilterActions.setAllAvailableFiltersFailed, (state) => ({
    ...state,
    allAvailableFilters: undefined,
  })),
  on(FilterActions.resetFilters, () => initialState),
  on(FilterActions.resetAppliedFilters, (state) => {
    const appliedFilters = {};
    return { ...state, appliedFilters };
  }),
  on(FilterActions.resetSelectedFilters, (state) => {
    const selectedFilters = {};
    return { ...state, selectedFilters };
  }),
  on(FilterActions.resetSelectedFiltersToAppliedFilters, (state) => {
    const selectedFilters =
      convertFiltersRecordToMap(state.appliedFilters).size > 0
        ? state.appliedFilters
        : {};
    return { ...state, selectedFilters };
  }),
  on(FilterActions.setSelectedFilters, (state, action) => {
    const selectedFilters: Record<string, FilterValue[]> = {};
    action.selectedFilters.forEach((value, key) => {
      selectedFilters[key] = value;
    });
    const newState = {
      ...state,
      selectedFilters,
    };
    return newState;
  }),
  on(FilterActions.setAppliedFilters, (state, action) => {
    const appliedFilters: Record<string, FilterValue[]> = {};
    action.appliedFilters.forEach((value, key) => {
      appliedFilters[key] = value;
    });

    const newState: FilterState = {
      ...state,
      appliedFilters,
    };
    return newState;
  }),
  on(
    FilterActions.setCreateAndSaveFilterCombinationIsLoading,
    (state, action) => {
      const newState = {
        ...state,
        saveFilterCombinationIsLoading: action.isLoading,
      };
      return newState;
    },
  ),
  on(FilterActions.setDeleteFilterCombinationIsLoading, (state, action) => {
    const newState = {
      ...state,
      deleteFilterCombinationIsLoading: action.isLoading,
    };
    return newState;
  }),
  on(FilterActions.setSavedFilterCombinations, (state, action) => {
    const newState = {
      ...state,
      savedFilterCombinations: action.savedFilterCombinations,
    };
    return newState;
  }),
  on(
    FilterActions.setFilterCombinationForecastSeries,
    (state: FilterState, action) => {
      const newState: FilterState = {
        ...state,
        filterCombinationForecastSeries: {
          ...state.filterCombinationForecastSeries,
          [action.savedFilterCombinationId]: action.forecastSeries,
        },
      };
      return newState;
    },
  ),
  on(
    FilterActions.removeFilterCombinationForecastSeries,
    (state: FilterState, action) => {
      const filterCombinationForecastSeries = {
        ...state.filterCombinationForecastSeries,
      };
      delete filterCombinationForecastSeries[action.filterCombinationId];
      const newState = {
        ...state,
        filterCombinationForecastSeries,
      };
      return newState;
    },
  ),
  on(
    FilterActions.setFilterCombinationForecastSeriesIdIsPending,
    (state: FilterState, action) => {
      const pendingFilterCombinationSeriesIds = [
        ...state.pendingFilterCombinationSeriesIds,
      ];
      const { isPending, filterCombinationId } = action;

      if (
        isPending &&
        !pendingFilterCombinationSeriesIds.includes(filterCombinationId)
      ) {
        pendingFilterCombinationSeriesIds.push(filterCombinationId);
      } else if (
        !isPending &&
        pendingFilterCombinationSeriesIds.includes(filterCombinationId)
      ) {
        pendingFilterCombinationSeriesIds.splice(
          pendingFilterCombinationSeriesIds.indexOf(filterCombinationId),
          1,
        );
      }
      const newState = {
        ...state,
        pendingFilterCombinationSeriesIds,
      };

      return newState;
    },
  ),
  on(FilterActions.resetFilterCombinationForecastSeries, (state) => {
    const newState = {
      ...state,
      filterCombinationForecastSeries: {},
    };
    return newState;
  }),
  on(FilterActions.setConsensusAppliedFilters, (state, action) => {
    const consensusAppliedFilters: Record<string, FilterValue[]> = {};
    action.consensusAppliedFilters.forEach((value, key) => {
      consensusAppliedFilters[key] = value;
    });
    return { ...state, consensusAppliedFilters };
  }),
  on(FilterActions.resetConsensusAppliedFilters, (state) =>
    Object.keys(state.consensusAppliedFilters).length === 0
      ? state
      : { ...state, consensusAppliedFilters: {} },
  ),
  on(FilterActions.setDemandSourceAppliedFilters, (state, action) => {
    const demandSourceAppliedFilters: Record<string, FilterValue[]> = {};
    action.demandSourceAppliedFilters.forEach((value, key) => {
      demandSourceAppliedFilters[key] = value;
    });
    return { ...state, demandSourceAppliedFilters };
  }),
  on(FilterActions.resetDemandSourceAppliedFilters, (state) => ({
    ...state,
    demandSourceAppliedFilters: {},
  })),
);
